import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { Observable, of, Subject } from 'rxjs';
import { TaskService } from '../../../task-management/services/task.service';
import { map, take } from 'rxjs/operators';
import { INavTab } from '../../models/nav-tab.interface';
import { PIPELINE_TABS, tabVisibleToUser } from '../../nav-tabs';
import { Router } from '@angular/router';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { Store } from '@ngxs/store';
import { TaskManagementState } from 'app/store/task-management/task-management.state';
import { GetUserActionItemTypes } from 'ng2-app/modules/today/state/user-action-items.action';
import { GetTodayBadgeCounts } from 'app/modules/today/state/today-badge-counts/today-badge-counts.action';
import { TodayBadgeCountsState } from 'app/modules/today/state/today-badge-counts/today-badge-counts.state';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  standalone: false,
})
export class NavbarComponent implements OnInit, OnDestroy {
  inboxBadgeCount$: Observable<number> = this.store.select(TaskManagementState.inboxCount);
  todayBadgeCount$: Observable<number> = this.store.select(TodayBadgeCountsState.total) as Observable<number>;

  @Input() user: LenderUser;

  pipelineTabs: INavTab[] = PIPELINE_TABS;

  visibleTabs$: Observable<INavTab[]>;
  activeTab: INavTab | null;
  destroyed$ = new Subject<void>();
  menuOpen = false;

  constructor(
    private location: Location,
    private taskService: TaskService,
    private router: Router,
    private store: Store,
  ) {}

  ngOnInit() {
    this.visibleTabs$ = of(
      this.pipelineTabs.filter(tabVisibleToUser(this.user)),
    );

    this.taskService
      .get({
        taskType: 'myTasks',
        callType: 'all',
        sortBy: {
          sortCol: 'created',
          sortDirection: 'desc',
        },
      })
      .pipe(take(1))
      .subscribe();

    // When the route changes, update the active tab accordingly
    this.location.onUrlChange(this.activateTabFromLocation.bind(this));

    // Use the current location to activate the current tab
    this.activateTabFromLocation();

    this.store.dispatch(new GetTodayBadgeCounts(this.user?.id));
    this.store.dispatch(new GetUserActionItemTypes());
  }

  ngOnDestroy() {
    this.destroyed$.next();
  }

  tabs() {
    return this.pipelineTabs;
  }

  onTabClick(tab: INavTab) {
    this.router.navigate([tab.uri]);
  }

  activateTab(tabToActivate: INavTab | undefined) {
    if (
      !tabToActivate ||
      !this.pipelineTabs?.some((tab) => tab === tabToActivate)
    ) {
      this.deactivateTabs();
    }
    this.visibleTabs$.pipe(take(1)).subscribe(() => {
      if (!tabToActivate) {
        return;
      }
      this.activeTab = tabToActivate;
    });
  }

  deactivateTabs() {
    this.activeTab = null;
  }

  route(tab: string) {
    console.log(tab);
  }

  displayBadgeCount(count) {
    if (count === 0) {
      return 0;
    }
    if (!count) {
      return null;
    }
    if (count > 100) {
      return 99;
    }
    if (count > 0) {
      return count;
    }
  }

  private activateTabFromLocation(path = '') {
    path ||= this.location.path();
    if (path.charAt(0) !== '/') {
      path = '/' + path;
    }
    this.tabFromPath(path).pipe(take(1)).subscribe(this.activateTab.bind(this));
  }

  private tabFromPath(path: string): Observable<INavTab | undefined> {
    return this.visibleTabs$.pipe(
      map((tabs) =>
        tabs.find((tab) => {
          const regexString = tab.uriRegex ? tab.uriRegex : tab.uri;
          const regex = new RegExp(`^${regexString}`, 'g');
          return regex.test(path);
        }),
      ),
    );
  }

  private tabWithKey(key: string): Observable<INavTab | undefined> {
    return this.visibleTabs$.pipe(
      map((tabs) => tabs.find((tab) => tab?.key === key)),
    );
  }
}

